import { BackLink } from "@components/ui/BackLink"
import { Badge } from "@components/ui/Badge"
import { Button } from "@components/ui/Button"
import { cn } from "@lib/cn"
import { formatGrade } from "@lib/intl"
import { AlertTriangle, Pencil } from "lucide-react"
import { memo } from "react"
import { useTranslation } from "react-i18next"

import { ProfilePersonaBlock } from "./ProfilePersonaBlock"

import { avatarSoft } from "@/constants/avatarTones"
import type { StudentProfileOverview, GradeScale } from "@/types"

interface Props {
  profile: StudentProfileOverview
  /** Where the professor came from, so "back" is honest. */
  backTo: string
  backLabel: string
  scale: GradeScale
  /** Opens the identity edit (PRD-40 §FR-19). */
  onEdit: () => void
  /** Opens the same edit at Personalización. */
  onEditPersona: () => void
}

export const ProfileHeader = memo(function ProfileHeader({
  profile,
  backTo,
  backLabel,
  scale,
  onEdit,
  onEditPersona,
}: Props) {
  const { t } = useTranslation("student-profile")
  const atRisk = profile.at_risk_subjects.length > 0

  return (
    <header className="rounded-card border-border bg-surface shadow-raised border p-4 md:p-5">
      {/* Was a 16px-tall link; BackLink is the one 44px shape (Etapa A). */}
      <BackLink to={backTo}>{backLabel}</BackLink>

      <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-3">
          <span
            className={cn(
              "flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-semibold",
              avatarSoft(profile.color),
            )}
            aria-hidden="true"
          >
            {profile.initials}
          </span>
          <div className="min-w-0">
            <h1 className="text-ink truncate text-xl font-bold md:text-2xl">{profile.name}</h1>
            <p className="text-ink-subtle mt-0.5 truncate text-sm">
              {[profile.email, profile.matricula].filter(Boolean).join(" · ")}
            </p>
          </div>
        </div>

        <div className="flex flex-col items-start gap-2 md:items-end">
          {/* The header is the identity card, so the identity edit lives on
              it — not buried in a subject card, where it would read as
              editing one enrollment. */}
          <Button type="button" variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            {t("header.edit")}
          </Button>
          {atRisk ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant="danger" className="gap-1 font-semibold">
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                {t("header.at_risk_in", { count: profile.at_risk_subjects.length })}
              </Badge>
              {profile.at_risk_subjects.map((subject) => (
                <Badge key={subject} variant="outline">
                  {subject}
                </Badge>
              ))}
            </div>
          ) : (
            <Badge variant="success" className="font-semibold">
              {t("header.on_track")}
            </Badge>
          )}
          {/* Deliberately plain text, never a colored KPI: a cross-subject
              average is not a signal a professor should act on (PRD-40
              §FR-11). It exists because parents ask for it. */}
          {profile.overall_avg_grade > 0 && (
            <p className="text-ink-subtle text-xs">
              {t("header.overall_average", {
                value: formatGrade(profile.overall_avg_grade, scale),
              })}
            </p>
          )}
        </div>
      </div>

      <ProfilePersonaBlock persona={profile.persona} onEdit={onEditPersona} />
    </header>
  )
})
